import { useCallback, useEffect, useRef, useState } from "react";

const FRAME_REMOVAL_DELAY = 10000;

/**
 * A hook to trigger an iframe leveraging a new `src` and manage its removal after a delay. An isLoading state
 * is provided to track the loading state of the iframe when possible. Some sources, such as a download url,
 * may not trigger the "load" event reliably across all browsers. A timeout is used as a fallback. I.e. this hook
 * can be used as a fire-and-forget hook.
 *
 * NOTE REGARDING IFRAME v. ANCHOR/NEW-WINDOW:
 * The `<a download>` approach works reliably for downloads when the browser can read the response — but it has a
 * critical limitation: the download attribute is ignored for cross-origin URLs. The browser will navigate to the
 * URL instead of downloading it, and whether that navigation triggers a download depends entirely on the
 * Content-Disposition header the server sends back.
 * The iframe approach sidesteps this entirely — it works for cross-origin presigned URLs (e.g. S3) because the
 * browser handles the navigation inside the iframe, and if the server responds with Content-Disposition: attachment,
 * the download triggers without any popup, regardless of origin.
 *
 * @param {number} [frameRemovalDelay = 10000] - The delay in milliseconds after which the iframe will be removed.
 *
 * @returns {object}
 *   @property {boolean} isLoadingSrc - The loading state of the iframe when setting the `src` attribute. NOTE: This
 *                                      may not be reliable across all browsers and does not indicate success or
 *                                      failure of the content.
 *   @property {function(src: string): Promise<void>} triggerIframe - A function to trigger the iframe.
 */
export const useTriggerIFrame = (frameRemovalDelay: number = FRAME_REMOVAL_DELAY) => {
  // This assignment and the following warning avoids "Rules of Hooks" violations, and ensures the hook works
  // regardless of the frameRemovalDelay value.
  const cleanRemovalDelay = Number.isFinite(frameRemovalDelay)
    ? Math.min(Math.max(frameRemovalDelay, 0), 60000)
    : FRAME_REMOVAL_DELAY;

  if (process.env.NODE_ENV !== "production" && cleanRemovalDelay !== frameRemovalDelay) {
    console.warn(
      `useTriggerIFrame: frameRemovalDelay "${frameRemovalDelay}" is out of range [0, 60000]; using ${cleanRemovalDelay}ms instead.`,
    );
  }

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // used for unmount and next trigger
  const [isLoadingSrc, setIsLoadingSrc] = useState(false);

  // Remove the iframe from the document body and reset the iframe reference.
  const removeIframe = useCallback(() => {
    if (iframeRef.current && document.body.contains(iframeRef.current)) {
      document.body.removeChild(iframeRef.current);
    }
    iframeRef.current = null;
  }, []);

  // Cleanup the timer and remove the iframe when the component unmounts.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      removeIframe();
    };
  }, [removeIframe]);

  // Trigger the iframe and manage its removal after a delay.
  const triggerIframe = useCallback(
    (src: string): Promise<void> => {
      return new Promise(resolve => {
        setIsLoadingSrc(true);

        // Cleanup the timer and remove the iframe when the iframe is loaded OR the timeout expires.
        if (timerRef.current) clearTimeout(timerRef.current);
        removeIframe();

        const iframe = document.createElement("iframe");

        if (process.env.NODE_ENV !== "production") {
          iframe.setAttribute("data-testid", "trigger-iframe");
        }

        iframe.style.display = "none";
        document.body.appendChild(iframe);
        iframeRef.current = iframe;

        // Setup the local timer for the specific invocation of the triggerIframe function.
        let timerId: ReturnType<typeof setTimeout> | undefined = undefined;

        // Cleanup the timer and remove the iframe when the iframe is loaded OR the timeout expires.
        // NOTE: The "load" event is not always fired reliably across all browsers.
        // Using a timeout as a fallback.
        const cleanup = () => {
          clearTimeout(timerId);
          iframe.removeEventListener("load", cleanup);
          if (iframeRef.current === iframe) {
            removeIframe();
            setIsLoadingSrc(false);
          }
          resolve();
        };

        iframe.addEventListener("load", cleanup, { once: true });
        timerId = timerRef.current = setTimeout(cleanup, cleanRemovalDelay);

        iframe.src = src;
      });
    },
    [cleanRemovalDelay, removeIframe],
  );

  return { isLoadingSrc, triggerIframe };
};
